import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { captureReferralFromURL } from "../lib/referral";

export default function Landing() {
  const { user, promptLogin } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [totalCount, setTotalCount] = React.useState(null);
  const [founder, setFounder] = React.useState(null);

  // Capture ?ref=CORP-XXXX on first render
  React.useEffect(() => {
    captureReferralFromURL();
  }, []);

  React.useEffect(() => {
    fetch(`${API}/stats/global`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && typeof d.total_translations === "number") setTotalCount(d.total_translations);
        if (d) setFounder(d);
      })
      .catch(() => {});
  }, []);

  const handleCTA = () => {
    if (user) {
      navigate("/app");
      return;
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Consent (CGU/RGPD) is collected via the modal in LoginConsentModal before redirect.
    promptLogin();
  };

  return (
    <main className="flex-grow" data-testid="landing-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
        <div className="md:col-span-7 flex flex-col gap-6">
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#FF3B30]">
            {t("hero_eyebrow")}
          </p>
          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl uppercase leading-[0.95] tracking-tighter text-[#0A0A0A]">
            {t("hero_title_1")}
            <br />
            <span className="bg-[#FFCC00] px-2 inline-block border-2 border-[#0A0A0A]">
              {t("hero_title_2")}
            </span>
            <br />
            {t("hero_title_3")}
          </h1>
          <p className="text-base md:text-lg max-w-xl text-[#0A0A0A] font-mono-corp leading-relaxed">
            {t("hero_subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center mt-2">
            <button
              data-testid="hero-cta-btn"
              onClick={handleCTA}
              className="bg-[#FF3B30] text-white border-2 border-[#0A0A0A] font-display text-base uppercase tracking-widest px-8 py-4 shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-brutal-sm active:translate-x-[6px] active:translate-y-[6px] active:shadow-none transition-all"
            >
              {t("hero_cta")} →
            </button>
            <p className="text-xs uppercase tracking-widest text-[#4B4B4B]">
              {t("hero_disclaimer")}
            </p>
          </div>
        </div>

        <div className="md:col-span-5 relative">
          <div className="bg-white border-2 border-[#0A0A0A] shadow-brutal overflow-hidden">
            <img
              src="https://images.pexels.com/photos/7640765/pexels-photo-7640765.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Frustrated office worker"
              data-testid="hero-image"
              className="w-full h-[380px] md:h-[460px] object-cover"
              style={{ filter: "grayscale(100%) contrast(120%)" }}
            />
            <div className="border-t-2 border-[#0A0A0A] px-4 py-3 flex justify-between items-center bg-[#0A0A0A] text-[#F4F4F0]">
              <span className="text-[10px] font-bold tracking-[0.3em] uppercase">
                INTERNAL_USE_ONLY
              </span>
              <span className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#FFCC00]">
                ● LIVE
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* GLOBAL COUNTER */}
      <section className="bg-[#FFCC00] border-y-2 border-[#0A0A0A]">
        <div
          data-testid="global-counter"
          className="max-w-7xl mx-auto px-6 md:px-12 py-8 md:py-10 flex flex-col md:flex-row items-center justify-center gap-3 md:gap-6 text-center"
        >
          <span
            data-testid="global-counter-value"
            className="font-display text-5xl sm:text-6xl md:text-7xl tracking-tighter text-[#0A0A0A] tabular-nums"
          >
            {totalCount === null ? "—" : totalCount.toLocaleString("fr-FR")}
          </span>
          <span className="text-xs md:text-sm font-bold tracking-[0.25em] uppercase text-[#0A0A0A] max-w-xs">
            {t("counter_label")}
          </span>
        </div>
      </section>

      {/* FOUNDER OFFER — shown while seats remain */}
      {founder && founder.founder_slots_remaining > 0 && (
        <section
          data-testid="founder-banner"
          className="bg-[#FF3B30] text-[#F4F4F0] border-y-2 border-[#0A0A0A]"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center">
            <span className="text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase">
              ★ {t("founder_badge")}
            </span>
            <span className="text-sm md:text-base font-bold uppercase tracking-wide">
              {t("founder_offer", {
                remaining: founder.founder_slots_remaining,
                pct: Math.round(founder.founder_next_discount * 100),
              })}
            </span>
          </div>
        </section>
      )}

      <section className="bg-[#0A0A0A] text-[#F4F4F0] border-y-2 border-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
          <div data-testid="sample-raw" className="flex flex-col gap-3">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#FF3B30] font-bold">
              [01] RAW
            </p>
            <p className="font-mono-corp text-base md:text-lg leading-relaxed">
              "Sérieux Karine, ton 'feedback' c'est juste de la jalousie déguisée.
              Tu veux qu'on bosse ensemble ? Apprends à lire un brief."
            </p>
          </div>
          <div data-testid="sample-translated" className="flex flex-col gap-3">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#FFCC00] font-bold">
              [02] CORPORATE OUTPUT
            </p>
            <p className="font-mono-corp text-base md:text-lg leading-relaxed">
              "Bonjour Karine, merci pour ton retour. Je note que nos lectures
              du brief diffèrent — je propose qu'on se cale 15 minutes pour
              réaligner nos compréhensions et maximiser nos synergies. Bien
              cordialement."
            </p>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20">
        <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tight text-[#0A0A0A] mb-10">
          // SPECS
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {[
            { num: "01", title: t("feature_1_title"), body: t("feature_1_body") },
            { num: "02", title: t("feature_2_title"), body: t("feature_2_body") },
            { num: "03", title: t("feature_3_title"), body: t("feature_3_body") },
          ].map((f) => (
            <div
              key={f.num}
              data-testid={`feature-card-${f.num}`}
              className="bg-white border-2 border-[#0A0A0A] p-6 shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-brutal-sm transition-all"
            >
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#FF3B30] font-bold mb-3">
                ITEM {f.num}
              </p>
              <h3 className="font-display text-xl uppercase mb-3">{f.title}</h3>
              <p className="font-mono-corp text-sm text-[#4B4B4B] leading-relaxed">
                {f.body}
              </p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
